import type React from "react";

import { Button } from "./button";

export interface FormActionsProps {
    /** Label for the primary (raised) action button. */
    primaryLabel: string;
    /** Handler invoked when the primary button is clicked. */
    onPrimary: () => void;
    /** Whether the primary button is disabled. */
    primaryDisabled?: boolean;
    /** Label for the cancel (transparent) action button. */
    cancelLabel: string;
    /** Handler invoked when the cancel button is clicked. */
    onCancel: () => void;
    /** Whether the cancel button is disabled. */
    cancelDisabled?: boolean;
    /** Button size, shared by BOTH buttons. Variants stay fixed (primary =
     *  raised/lifted, cancel = transparent/underline). Defaults to "md". */
    size?: "xs" | "fit" | "sm" | "md" | "lg";
    /** Extra classes appended to the root row. */
    className?: string;
    /** Sim-only cursor key placed on the primary button. Inert in production. */
    primaryCursorKey?: string;
    /** Sim-only — force the primary button's hover visual. */
    primaryForceHover?: boolean;
    /** Sim-only — force the primary button's pressed visual. */
    primaryForcePressed?: boolean;
}

/**
 * Editorial primary + cancel action pair — a raised primary button beside an
 * underlined transparent cancel button. Purely presentational: all disabled
 * logic and handlers are supplied by the consumer.
 */
export function FormActions({
    primaryLabel,
    onPrimary,
    primaryDisabled,
    cancelLabel,
    onCancel,
    cancelDisabled,
    size = "md",
    className,
    primaryCursorKey,
    primaryForceHover = false,
    primaryForcePressed = false
}: FormActionsProps): React.ReactElement {
    return (
        <div
            className={`flex items-center gap-3${
                className ? ` ${className}` : ""
            }`}
        >
            <Button
                variant="raised"
                interaction="lifted"
                size={size}
                disabled={primaryDisabled}
                onClick={onPrimary}
                data-sim-cursor-key={primaryCursorKey}
                forceHover={primaryForceHover}
                forcePressed={primaryForcePressed}
            >
                {primaryLabel}
            </Button>
            <Button
                variant="transparent"
                size={size}
                className="underline"
                disabled={cancelDisabled}
                onClick={onCancel}
            >
                {cancelLabel}
            </Button>
        </div>
    );
}
